import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { s } from "../style.js";
import StatusPill from "./StatusPill";
import type { Task, TaskLog, TaskStatus, Role, User, Repo } from "../types";
import { fetchTaskLogs, createTaskLog, updateTaskLog, deleteTaskLog, fetchRepos, fetchUsers } from "../api";

const ACTIONS = [
  { label: "Backlog", status: "Backlog" },
  { label: "In progress", status: "In progress" },
  { label: "Move to staging", status: "Staging" },
  { label: "On hold", status: "On hold" },
  { label: "Mark done", status: "Done" },
];

const kickerStyle = s(
  "font-size:14px; letter-spacing:0.14em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600)"
);

const metaLabel = s(
  "font-size:12px; letter-spacing:0.12em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; margin-bottom:4px"
);

const formatTimeAgo = (isoString: string) => {
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(isoString).toLocaleDateString();
};

export default function TaskSheet({ task, onClose, onUpdate, auth, user, onToast }: { task: Task; onClose: () => void; onUpdate: (id: string, updates: { status?: TaskStatus; repos?: string[]; ownerId?: string | null }) => void; auth: Role; user: User | null; onToast: (message: string, type: "success" | "error") => void }) {
  const [mounted, setMounted] = useState(false);
  const [drag, setDrag] = useState(0);
  const [logs, setLogs] = useState<TaskLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteSending, setNoteSending] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingSending, setEditingSending] = useState(false);
  const [availableRepos, setAvailableRepos] = useState<Repo[]>([]);
  const [editingRepos, setEditingRepos] = useState(false);
  const [selectedRepos, setSelectedRepos] = useState<string[]>([]);
  const [repoDropdownOpen, setRepoDropdownOpen] = useState(false);
  const [adminUsers, setAdminUsers] = useState<User[]>([]);
  const [editingOwner, setEditingOwner] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null);
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const closingRef = useRef(false);
  const repoDropdownRef = useRef<HTMLDivElement | null>(null);
  const ownerDropdownRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const dragRef = useRef(0);
  const startRef = useRef({ x: 0 });

  const isAdmin = auth === "admin";
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (!task) return;
    setLogs([]);
    setShowAll(false);
    setLogsLoading(true);
    fetchTaskLogs(task.id)
      .then((data) => setLogs(data))
      .catch(() => setLogs([]))
      .finally(() => setLogsLoading(false));
  }, [task?.id, task?.updatedAt]);

  useEffect(() => {
    fetchRepos()
      .then((data) => setAvailableRepos(data.filter((r: Repo) => r.active)))
      .catch(() => setAvailableRepos([]));
    fetchUsers()
      .then((data) => setAdminUsers(data))
      .catch(() => setAdminUsers([]));
  }, []);

  useEffect(() => {
    if (!repoDropdownOpen && !ownerDropdownOpen) return;
    const onPointer = (e: PointerEvent) => {
      if (repoDropdownRef.current && !repoDropdownRef.current.contains(e.target as Node)) setRepoDropdownOpen(false);
      if (ownerDropdownRef.current && !ownerDropdownRef.current.contains(e.target as Node)) setOwnerDropdownOpen(false);
    };
    window.addEventListener("pointerdown", onPointer);
    return () => window.removeEventListener("pointerdown", onPointer);
  }, [repoDropdownOpen, ownerDropdownOpen]);

  const startEditRepos = () => {
    setSelectedRepos([...task.repos]);
    setEditingRepos(true);
  };

  const cancelEditRepos = () => {
    setEditingRepos(false);
    setSelectedRepos([]);
    setRepoDropdownOpen(false);
  };

  const saveRepos = () => {
    onUpdate(task.id, { repos: selectedRepos });
    setEditingRepos(false);
    setRepoDropdownOpen(false);
  };

  const startEditOwner = () => {
    setSelectedOwnerId(task.ownerId || null);
    setEditingOwner(true);
  };

  const cancelEditOwner = () => {
    setEditingOwner(false);
    setSelectedOwnerId(null);
    setOwnerDropdownOpen(false);
  };

  const saveOwner = () => {
    onUpdate(task.id, { ownerId: selectedOwnerId });
    setEditingOwner(false);
    setOwnerDropdownOpen(false);
  };

  const requestClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setDrag(0);
    setMounted(false);
    setTimeout(onClose, 240);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onPointerDown = (e: any) => {
    if ((e.target as HTMLElement).closest("button")) return;
    startRef.current = { x: e.clientX };
    draggingRef.current = true;
  };
  const onPointerMove = (e: any) => {
    if (!draggingRef.current) return;
    let dx = e.clientX - startRef.current.x;
    if (dx < 0) dx = 0;
    dragRef.current = dx;
    setDrag(dx);
  };
  const onPointerUp = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (dragRef.current > 120) requestClose();
    else setDrag(0);
  };

  const addNote = async () => {
    const trimmed = noteText.trim();
    if (!trimmed) return;
    setNoteSending(true);
    setNoteError("");
    try {
      const created = await createTaskLog(task.id, trimmed);
      setLogs((prev) => [{ ...created, details: created.details || null }, ...prev]);
      setNoteText("");
      onToast("Note added", "success");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Network error";
      setNoteError(message);
      onToast(message, "error");
    } finally {
      setNoteSending(false);
    }
  };

  const startEdit = (log: TaskLog) => {
    setEditingId(log.id);
    setEditingText(log.content || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const saveEdit = async () => {
    const trimmed = editingText.trim();
    if (!trimmed) return;
    setEditingSending(true);
    try {
      const updated = await updateTaskLog(task.id, editingId!, trimmed);
      setLogs((prev) => prev.map((l) => (l.id === editingId ? { ...l, content: updated.content, updatedAt: updated.updatedAt } : l)));
      setEditingId(null);
      setEditingText("");
      onToast("Note updated", "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : "Note update failed", "error");
    } finally {
      setEditingSending(false);
    }
  };

  const deleteNote = async (logId: string) => {
    try {
      await deleteTaskLog(task.id, logId);
      setLogs((prev) => prev.filter((l) => l.id !== logId));
      onToast("Note deleted", "success");
    } catch (error) {
      onToast(error instanceof Error ? error.message : "Note deletion failed", "error");
    }
  };

  const transform =
    drag > 0 ? `translateX(${drag}px)` : mounted ? "translateX(0)" : "translateX(100%)";
  const transition = drag > 0 ? "none" : "transform 240ms cubic-bezier(0.32,0.72,0,1)";

  return (
    <div style={s("position:absolute; inset:0; z-index:40")}>
      <div
        style={{
          ...s(
            "position:absolute; inset:0; background:color-mix(in srgb, var(--color-neutral-900) 50%, transparent)"
          ),
          opacity: mounted ? 1 : 0,
          transition: "opacity 240ms ease",
        }}
        onClick={requestClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          ...s(
            "position:absolute; top:0; right:0; bottom:0; width:440px; background:var(--color-surface); color:var(--color-text); display:flex; flex-direction:column; border-left:2px solid var(--color-divider)"
          ),
          transform,
          transition,
          touchAction: "pan-y",
        }}
      >
        <div
          style={s(
            "flex:none; display:flex; align-items:center; gap:12px; padding:18px 22px; border-bottom:2px solid var(--color-divider)"
          )}
        >
          <span style={kickerStyle}>ATL-{task.id}</span>
          <StatusPill task={task} />
          <div style={s("flex:1")} />
          <button
            onClick={requestClose}
            aria-label="Close"
            style={s(
              "all:unset; cursor:pointer; width:44px; height:44px; display:flex; align-items:center; justify-content:center; font-size:30px; line-height:1; color:var(--color-neutral-600)"
            )}
          >
            x
          </button>
        </div>

        <div style={s("flex:1; min-height:0; overflow:auto; padding:24px 22px")}>
          <div
            style={s(
              "font-size:34px; line-height:1.08; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.02em; text-wrap:pretty"
            )}
          >
            {task.title}
          </div>

          <div ref={repoDropdownRef} style={s("position:relative; margin-top:16px")}>
            <div style={s("display:flex; align-items:center; gap:8px; margin-bottom:6px")}>
              <div style={metaLabel}>Repos</div>
              {isAdmin && !editingRepos && (
                <button
                  onClick={startEditRepos}
                  style={s("all:unset; cursor:pointer; font-size:10px; color:var(--color-accent); text-transform:uppercase; font-family:var(--font-heading); font-weight:800; letter-spacing:0.06em")}
                >
                  Edit
                </button>
              )}
            </div>

            {editingRepos ? (
              <div>
                <button
                  type="button"
                  onClick={() => setRepoDropdownOpen((v) => !v)}
                  style={s(
                    "cursor:pointer; width:100%; min-height:46px; padding:8px 14px; display:flex; flex-wrap:wrap; align-items:center; gap:6px; text-align:left; color:var(--color-text); background:var(--color-bg); border:2px solid var(--color-divider); font:inherit; box-sizing:border-box"
                  )}
                >
                  {selectedRepos.length === 0 ? (
                    <span style={s("color:var(--color-neutral-600); font-size:14px")}>Select repos</span>
                  ) : (
                    selectedRepos.map((id) => {
                      const r = availableRepos.find((x) => x.id === id);
                      return (
                        <span
                          key={id}
                          style={s(
                            "display:inline-flex; align-items:center; padding:3px 10px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-neutral-200); color:var(--color-neutral-800)"
                          )}
                        >
                          {r?.name || id}
                        </span>
                      );
                    })
                  )}
                  <span style={s("flex:1")} />
                  <span style={s("font-size:11px; color:var(--color-neutral-600)")}>{repoDropdownOpen ? "▴" : "▾"}</span>
                </button>

                {repoDropdownOpen && (
                  <div
                    style={s(
                      "position:absolute; top:100%; left:0; right:0; margin-top:4px; background:var(--color-surface); border:2px solid var(--color-divider); z-index:10; max-height:220px; overflow:auto"
                    )}
                  >
                    {availableRepos.length === 0 ? (
                      <div style={s("padding:10px 14px; font-size:14px; color:var(--color-neutral-600)")}>No repos</div>
                    ) : (
                      availableRepos.map((r) => {
                        const checked = selectedRepos.includes(r.id);
                        return (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() =>
                              setSelectedRepos((prev) =>
                                checked ? prev.filter((x) => x !== r.id) : [...prev, r.id]
                              )
                            }
                            style={s(
                              "all:unset; cursor:pointer; display:flex; align-items:center; gap:10px; width:100%; padding:10px 14px; box-sizing:border-box; font-size:14px; color:var(--color-text)"
                            )}
                          >
                            <span
                              style={{
                                ...s(
                                  "flex:none; width:16px; height:16px; border:2px solid var(--color-divider); display:flex; align-items:center; justify-content:center"
                                ),
                                background: checked ? "var(--color-text)" : "transparent",
                              }}
                            >
                              {checked && (
                                <span style={s("display:block; width:8px; height:8px; background:var(--color-bg)")} />
                              )}
                            </span>
                            <span>{r.name}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}

                <div style={s("display:flex; gap:8px; margin-top:8px")}>
                  <button
                    onClick={saveRepos}
                    style={s("all:unset; cursor:pointer; padding:6px 14px; background:var(--color-text); color:var(--color-bg); font-family:var(--font-heading); font-weight:800; font-size:11px; letter-spacing:0.06em; text-transform:uppercase")}
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelEditRepos}
                    style={s("all:unset; cursor:pointer; padding:6px 14px; font-size:11px; color:var(--color-neutral-600); text-transform:uppercase; font-family:var(--font-heading); font-weight:800; letter-spacing:0.06em")}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={s("display:flex; flex-wrap:wrap; gap:6px")}>
                {task.repos.length === 0 ? (
                  <span style={s("font-size:14px; color:var(--color-neutral-500)")}>None</span>
                ) : (
                  (task.repoNames || task.repos).map((repo, i) => (
                    <span
                      key={task.repos[i]}
                      style={s(
                        "display:inline-flex; align-items:center; padding:3px 10px; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--color-neutral-200); color:var(--color-neutral-800)"
                      )}
                    >
                      {repo}
                    </span>
                  ))
                )}
              </div>
            )}
          </div>

          <div ref={ownerDropdownRef} style={s("position:relative; margin-top:20px")}>
            <div style={s("display:flex; align-items:center; gap:8px; margin-bottom:6px")}>
              <div style={metaLabel}>Owner</div>
              {isAdmin && !editingOwner && (
                <button
                  onClick={startEditOwner}
                  style={s("all:unset; cursor:pointer; font-size:10px; color:var(--color-accent); text-transform:uppercase; font-family:var(--font-heading); font-weight:800; letter-spacing:0.06em")}
                >
                  Edit
                </button>
              )}
            </div>

            {editingOwner ? (
              <div>
                <button
                  type="button"
                  onClick={() => setOwnerDropdownOpen((v) => !v)}
                  style={s(
                    "cursor:pointer; width:100%; min-height:46px; padding:8px 14px; display:flex; align-items:center; gap:6px; text-align:left; color:var(--color-text); background:var(--color-bg); border:2px solid var(--color-divider); font:inherit; box-sizing:border-box"
                  )}
                >
                  <span style={selectedOwnerId ? s("font-size:15px") : s("color:var(--color-neutral-600); font-size:15px")}>
                    {selectedOwnerId ? (adminUsers.find((u) => u.id === selectedOwnerId)?.name ?? "Select owner") : "Unassigned"}
                  </span>
                  <span style={s("flex:1")} />
                  <span style={s("font-size:11px; color:var(--color-neutral-600)")}>{ownerDropdownOpen ? "▴" : "▾"}</span>
                </button>

                {ownerDropdownOpen && (
                  <div
                    style={s(
                      "position:absolute; top:100%; left:0; right:0; margin-top:4px; background:var(--color-surface); border:2px solid var(--color-divider); z-index:10; max-height:220px; overflow:auto"
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => { setSelectedOwnerId(null); setOwnerDropdownOpen(false); }}
                      style={{
                        ...s(
                          "all:unset; cursor:pointer; display:flex; align-items:center; width:100%; padding:10px 14px; box-sizing:border-box; font-size:14px; color:var(--color-neutral-600)"
                        ),
                        background: selectedOwnerId === null ? "var(--color-neutral-200)" : "transparent",
                      }}
                    >
                      Unassigned
                    </button>
                    {adminUsers.map((u) => {
                      const active = selectedOwnerId === u.id;
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => { setSelectedOwnerId(u.id); setOwnerDropdownOpen(false); }}
                          style={{
                            ...s(
                              "all:unset; cursor:pointer; display:flex; align-items:center; width:100%; padding:10px 14px; box-sizing:border-box; font-size:14px; color:var(--color-text)"
                            ),
                            background: active ? "var(--color-neutral-200)" : "transparent",
                          }}
                        >
                          {u.name}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div style={s("display:flex; gap:8px; margin-top:8px")}>
                  <button
                    onClick={saveOwner}
                    style={s("all:unset; cursor:pointer; padding:6px 14px; background:var(--color-text); color:var(--color-bg); font-family:var(--font-heading); font-weight:800; font-size:11px; letter-spacing:0.06em; text-transform:uppercase")}
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelEditOwner}
                    style={s("all:unset; cursor:pointer; padding:6px 14px; font-size:11px; color:var(--color-neutral-600); text-transform:uppercase; font-family:var(--font-heading); font-weight:800; letter-spacing:0.06em")}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={s("display:flex; align-items:center; gap:10px")}>
                <span
                  style={s(
                    "width:40px; height:40px; flex:none; background:var(--color-text); color:var(--color-bg); display:flex; align-items:center; justify-content:center; font-family:var(--font-heading); font-weight:800; font-size:14px"
                  )}
                >
                  {task.initials || "?"}
                </span>
                <span style={s("font-size:18px; font-family:var(--font-heading); font-weight:800")}>
                  {task.owner || "Unassigned"}
                </span>
              </div>
            )}
          </div>

          <div
            style={s(
              "margin-top:20px; padding-top:20px; border-top:2px solid var(--color-divider); font-size:16px; line-height:1.5; color:var(--color-text)"
            )}
          >
            {task.description}
          </div>

          {task.aiPrompt && (
            <div style={s("margin-top:20px; padding-top:20px; border-top:2px solid var(--color-divider)")}>
              <div style={s("display:flex; align-items:center; justify-content:space-between; margin-bottom:8px")}>
                <div style={metaLabel}>AI Prompt</div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(task.aiPrompt || "");
                    onToast("Prompt copied", "success");
                  }}
                  style={s("all:unset; cursor:pointer; display:flex; align-items:center; gap:4px; padding:4px 10px; font-size:10px; letter-spacing:0.06em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-accent); border:1px solid var(--color-accent)")}
                >
                  Copy
                </button>
              </div>
              <pre style={s("margin:0; padding:14px; overflow:auto; white-space:pre-wrap; overflow-wrap:anywhere; font-family:monospace; font-size:12px; line-height:1.5; color:var(--color-text); background:var(--color-surface); border:2px solid var(--color-divider)")}>
                {task.aiPrompt}
              </pre>
            </div>
          )}

          <div
            style={s(
              "margin-top:20px; padding-top:20px; border-top:2px solid var(--color-divider)"
            )}
          >
            <div style={metaLabel}>Branch</div>
            <div
              style={s(
                "font-size:15px; font-family:var(--font-heading); font-weight:800; letter-spacing:0.02em"
              )}
            >
              {task.branch}
            </div>

            <div style={s("display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-top:16px")}>
              <div>
                <div style={metaLabel}>Created</div>
                <div style={s("font-size:16px; font-variant-numeric:tabular-nums")}>
                  {task.created}
                </div>
              </div>
              <div>
                <div style={metaLabel}>Updated</div>
                <div style={s("font-size:16px; font-variant-numeric:tabular-nums")}>
                  {task.updated}
                </div>
              </div>
            </div>
          </div>

          <div style={s("margin-top:24px; border-top:2px solid var(--color-neutral-200); padding-top:16px")}>
            <div style={s("font-weight:800; font-size:11px; text-transform:uppercase; letter-spacing:0.08em; color:var(--color-neutral-600); margin-bottom:12px")}>
              Dev Logs
            </div>

            {isAdmin && (
              <div style={s("margin-bottom:16px")}>
                <textarea
                  value={noteText}
                  onChange={(e) => { setNoteText(e.target.value); setNoteError(""); }}
                  placeholder="Add a note, finding, or update..."
                  maxLength={500}
                  style={s("width:100%; min-height:80px; padding:10px 14px; font:inherit; color:var(--color-text); background:var(--color-surface); border:2px solid var(--color-divider); resize:vertical; box-sizing:border-box")}
                />
                <div style={s("display:flex; align-items:center; gap:10px; margin-top:8px")}>
                  <button
                    onClick={addNote}
                    disabled={noteSending || !noteText.trim()}
                    style={{
                      ...s(
                        "all:unset; cursor:pointer; display:inline-flex; align-items:center; gap:6px; padding:8px 16px; background:var(--color-text); color:var(--color-bg); font-family:var(--font-heading); font-weight:800; font-size:12px; letter-spacing:0.08em; text-transform:uppercase"
                      ),
                      opacity: noteSending || !noteText.trim() ? 0.5 : 1,
                      cursor: noteSending || !noteText.trim() ? "default" : "pointer",
                    }}
                  >
                    {noteSending && <Loader2 size={12} className="animate-spin" />}
                    Add note
                  </button>
                  <span style={s("font-size:11px; color:var(--color-neutral-600)")}>
                    {noteText.length}/500
                  </span>
                  {noteError && (
                    <span style={s("font-size:11px; color:var(--color-accent)")}>{noteError}</span>
                  )}
                </div>
              </div>
            )}

            {logsLoading ? (
              <div style={s("display:flex; align-items:center; gap:8px; padding:12px 0; color:var(--color-neutral-600)")}>
                <Loader2 size={14} className="animate-spin" />
                <span style={s("font-size:12px")}>Loading...</span>
              </div>
            ) : (
              (showAll ? logs : logs.slice(0, 5)).map((log) => {
                const isDevLog = log.action === "dev_log";
                const isEditing = editingId === log.id;
                const isOwner = isDevLog && isAdmin && user && log.actor === (user.username || "admin");

                return (
                  <div key={log.id} style={s("display:flex; gap:10px; margin-bottom:14px; align-items:flex-start")}>
                    <div style={s("width:28px; height:28px; background:var(--color-neutral-200); display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:800; flex-shrink:0")}>
                      {log.actorInitials}
                    </div>
                    <div style={s("flex:1; min-width:0")}>
                      {isDevLog ? (
                        <div style={s("display:flex; align-items:baseline; gap:6px; margin-bottom:2px")}>
                          <span style={s("font-size:10px; letter-spacing:0.1em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-accent)")}>
                            Dev note
                          </span>
                          {log.updatedAt && (
                            <span style={s("font-size:10px; color:var(--color-neutral-600)")}>
                              (edited)
                            </span>
                          )}
                        </div>
                      ) : null}

                      {isEditing ? (
                        <div>
                          <textarea
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            maxLength={500}
                            autoFocus
                            style={s("width:100%; min-height:60px; padding:8px 10px; font:inherit; font-size:13px; color:var(--color-text); background:var(--color-surface); border:2px solid var(--color-divider); resize:vertical; box-sizing:border-box")}
                          />
                          <div style={s("display:flex; gap:8px; margin-top:6px")}>
                            <button
                              onClick={saveEdit}
                              disabled={editingSending || !editingText.trim()}
                              style={{
                                ...s("all:unset; cursor:pointer; padding:4px 10px; background:var(--color-text); color:var(--color-bg); font-family:var(--font-heading); font-weight:800; font-size:11px; letter-spacing:0.06em; text-transform:uppercase"),
                                opacity: editingSending || !editingText.trim() ? 0.5 : 1,
                              }}
                            >
                              {editingSending ? "Saving..." : "Save"}
                            </button>
                            <button
                              onClick={cancelEdit}
                              disabled={editingSending}
                              style={s("all:unset; cursor:pointer; padding:4px 10px; font-size:11px; color:var(--color-neutral-600); text-transform:uppercase; font-family:var(--font-heading); font-weight:800; letter-spacing:0.06em")}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : isDevLog ? (
                        <div style={s("font-size:13px; line-height:1.45; white-space:pre-wrap; word-break:break-word")}>
                          {log.content}
                        </div>
                      ) : (
                        <div style={s("font-size:13px")}>
                          {log.action === "created" ? "Created" : `Status changed to ${log.details?.to}`}
                        </div>
                      )}

                      <div style={s("display:flex; align-items:center; gap:8px; margin-top:4px")}>
                        <span style={s("font-size:11px; color:var(--color-neutral-600)")}>
                          {log.actor} · {formatTimeAgo(log.createdAt)}
                        </span>
                        {isDevLog && isOwner && !isEditing && (
                          <>
                            <button
                              onClick={() => startEdit(log)}
                              style={s("all:unset; cursor:pointer; font-size:10px; color:var(--color-neutral-600); text-transform:uppercase; font-family:var(--font-heading); font-weight:800; letter-spacing:0.06em")}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteNote(log.id)}
                              style={s("all:unset; cursor:pointer; font-size:10px; color:var(--color-accent); text-transform:uppercase; font-family:var(--font-heading); font-weight:800; letter-spacing:0.06em")}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {!logsLoading && logs.length > 5 && !showAll && (
              <button onClick={() => setShowAll(true)} style={s("font-size:12px; color:var(--color-accent); background:none; border:none; cursor:pointer; padding:0")}>
                Show more ({logs.length - 5} more)
              </button>
            )}
          </div>
        </div>

        <div
          style={s(
            "flex:none; display:grid; grid-template-columns:1fr 1fr; gap:12px; padding:18px 22px; border-top:2px solid var(--color-divider)"
          )}
        >
          {ACTIONS.map((a) => {
            const active = task.status === a.status;
            return (
              <button
                key={a.status}
                disabled={active}
                onClick={() => onUpdate(task.id, { status: a.status as TaskStatus })}
                style={{
                  ...s(
                    "all:unset; cursor:pointer; display:flex; align-items:center; justify-content:center; padding:12px 14px; border:2px solid var(--color-divider); font-family:var(--font-heading); font-weight:800; font-size:13px; letter-spacing:0.08em; text-transform:uppercase; text-align:center"
                  ),
                  background: active ? "var(--color-neutral-200)" : "transparent",
                  color: active ? "var(--color-neutral-600)" : "var(--color-text)",
                  cursor: active ? "default" : "pointer",
                }}
              >
                {a.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
